import { Grid, Section } from "../../components/section";
// 2. Import the action icons
import {
  IoOpenOutline,
  IoPencilOutline,
  IoTrashOutline,
} from "react-icons/io5";
// 1. Ensure deleteJob is imported from your Redux slice
import { deleteJob, fetchApplications, fetchJobs } from "../../redux/careers";
import { useDispatch, useSelector } from "react-redux";

import DataTable from "react-data-table-component";
import { ErrorBanner } from "../../components/banner";
import { JobSummaryChart } from "../../components/charts/ChartJobSummary";
import { Link } from "react-router-dom";
import { Loading } from "../../components/loader";
import Moment from "moment";
import { OverviewChart } from "../../components/charts/ChartOverview";
import { Title } from "../../components/typography";
import { shadow } from "../../assets/styles/parameters";
import styled from "styled-components";
import { useEffect } from "react";

export const CareersList = () => {
  const { loading, error, jobs, applications } = useSelector(
    (state) => state.careers,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchJobs());
    dispatch(fetchApplications());
  }, [dispatch]);

  // --- ACTIONS ---

  // 3. Add a direct delete handler so admins can delete right from the table
  const handleDeleteJob = (id, title) => {
    if (
      window.confirm(
        `Are you sure you want to delete the job posting: "${title}"?`,
      )
    ) {
      dispatch(deleteJob(id));
    }
  };

  // --- TABLE COLUMNS ---

  const jobsColumn = [
    {
      name: "Date",
      selector: (row) => Moment(row.postedOn).format("MMM DD, YYYY"),
      sortable: true,
      width: "120px",
    },
    {
      name: "Job Title",
      selector: (row) => row.title,
      sortable: true,
      style: { fontWeight: 600, color: "#111" }, // Made the title stand out more
    },
    {
      name: "Type",
      selector: (row) => row.jobType?.replace("_", " "),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <StatusBadge $status={row.status}>{row.status}</StatusBadge>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionFlex>
          {/* Edit Button routes to your existing CareersEdit page */}
          <ActionLink to={`edit/${row.id}`} state={row} title="Edit Job">
            <IoPencilOutline size={18} />
          </ActionLink>

          {/* Delete Button instantly triggers Redux */}
          <ActionButton
            onClick={() => handleDeleteJob(row.id, row.title)}
            title="Delete Job"
            color="#dc3545"
            bg="#FDEDEC"
          >
            <IoTrashOutline size={18} />
          </ActionButton>
        </ActionFlex>
      ),
      button: true,
      width: "100px",
    },
  ];

  const applicantColumn = [
    {
      name: "Date",
      selector: (row) => Moment(row.createdDate).format("MMM DD, YYYY"),
      sortable: true,
      width: "120px",
    },
    {
      name: "Candidate Name",
      selector: (row) =>
        `${row.candidate?.firstName} ${row.candidate?.lastName}`,
      sortable: true,
      style: { fontWeight: 600, color: "#111" },
    },
    {
      name: "Applied For",
      selector: (row) => row.job?.title || "Unknown Role",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        // Added a quick link to review the application directly from the dashboard
        <ActionLink
          to={`applications/edit/${row.id}`}
          state={row}
          title="Review Application"
        >
          Review <IoOpenOutline style={{ marginLeft: "4px" }} />
        </ActionLink>
      ),
      button: true,
      width: "110px",
    },
  ];

  if (loading && jobs.length === 0) {
    return <Loading />;
  }

  return (
    <>
      <Section>
        {error && <ErrorBanner text={error} />}

        <Data>
          <Container>
            <JobSummaryChart jobs={jobs} />
          </Container>
          <Container>
            <OverviewChart jobs={jobs} applications={applications} />
          </Container>
        </Data>

        <Grid>
          <Container>
            <Title number="5">Recent Roles</Title>
            <DataTable
              columns={jobsColumn}
              data={jobs.slice(0, 10)}
              striped
              highlightOnHover
              compact
              fixedHeader
              responsive
              customStyles={tableStyles}
            />
          </Container>
          <Container>
            <Title number="5">Recent Applicants</Title>
            <DataTable
              columns={applicantColumn}
              data={applications.slice(0, 10)}
              striped
              highlightOnHover
              compact
              fixedHeader
              responsive
              customStyles={tableStyles}
            />
          </Container>
        </Grid>
      </Section>
    </>
  );
};

// --- STYLED COMPONENTS ---

const Data = styled.div`
  display: grid;
  grid-template-columns: 0.3fr 0.7fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Container = styled.div`
  background-color: white;
  box-shadow: ${shadow.light};
  padding: 1.5rem 2rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 1.5rem;
  /* Ensure container never overflows its grid cell */
  min-width: 0;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 640px) {
    padding: 1rem;
  }
`;

const ActionFlex = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.color};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${(props) => props.bg};
  }
`;

const ActionLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b5bdb;
  text-decoration: none;
  padding: 6px;
  border-radius: 6px;
  transition: background 0.2s;

  &:hover {
    background: #e6f2ff;
  }
`;

const StatusBadge = styled.span`
  background: ${(props) => {
    switch (props.$status) {
      case "OPEN":
        return "#E8F8F5";
      case "CLOSED":
        return "#FDEDEC";
      case "ARCHIVED":
        return "#F3F4F6";
      default:
        return "#F3F4F6";
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case "OPEN":
        return "#2ECA7F";
      case "CLOSED":
        return "#E74C3C";
      case "ARCHIVED":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  }};
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 700;
`;

const tableStyles = {
  headRow: {
    style: { borderBottomColor: "#f0f0f0", fontWeight: 600, color: "#555" },
  },
  rows: {
    style: {
      minHeight: "52px",
      "&:not(:last-of-type)": { borderBottomColor: "#f0f0f0" },
    },
  },
};
